import React from 'react'

import clone from 'lodash/clone'
import get from 'lodash/get'
import set from 'lodash/set'
import unset from 'lodash/unset'
import isNil from 'lodash/isNil'
import isObject from 'lodash/isObject'

import Observer from './Observer'
import { isChildPath } from './util'

/**
 * An observer around an object
 * When a value inside the object is updated, the dependencies of the related paths are notified
 */
export default class ObjectObserver {
  observers = {}
  object

  constructor(object = {}) {
    this.object = object
  }

  /**
   * Notify every observers related to a given path
   *
   * @param {String} path - the path to notify
   */
  notify(path) {
    Object.keys(this.observers)
      // filter with observer paths that are the parent path or the child path of the notified path
      .filter(key => isChildPath(path, key) || isChildPath(key, path))
      // notify each observer with their value and the original notified path
      .forEach(key => this.observers[key].notify(path))
  }

  get(path) {
    return path ? get(this.object, path) : this.object
  }

  set(value, path = '') {
    // if value is a function get the value from the function
    value = value instanceof Function ? value(this.get(path)) : value
    //  if path exists  set the value at the given path in the existing object
    if (path) {
      this.object = isObject(this.object) ? this.object : {}
      set(this.object, path, value)
    }
    // else replace the object with the value
    else this.object = value
    // finally notify that the object has changed at that path
    this.notify(path)
  }

  unset(path) {
    path ? unset(this.object, path) : (this.object = {})
    this.notify(path)
  }

  subscribe(listener, ...paths) {
    // if no path is provided, subscribe to root path
    paths = paths.filter(Boolean).length ? paths : ['']
    // register subscriptions for each path
    const unsubscribers = paths.map(path => {
      this.observers[path] = this.observers[path] || new Observer()
      // for each path, provide the associated value as argument
      return this.observers[path].subscribe(path =>
        listener(...paths.map(path => this.get(path)), { path, observer: this })
      )
    })
    // return unsubscribing function
    return () => unsubscribers.forEach(unsubscribe => unsubscribe())
  }

  /**
   * A hook to use in react components that declare a subscription to one or many paths
   * @param {Function} listener
   * @param {String} path
   */
  useSubscribe(listener, ...paths) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => this.subscribe(listener, ...paths), [...paths])
  }

  /**
   * A hook to use in react components that return an always up to date value at a specific path
   * @param {String} path
   * @param {*} defaultValue
   */
  useValue(path, defaultValue) {
    // create local count state
    const [count, update] = React.useReducer(state => state + 1, 0)

    // subscribe to observable
    this.useSubscribe(() => update(), path)

    // return new version when count changes
    return React.useMemo(
      () => {
        const value = this.get(path)
        return isNil(value) ? defaultValue : clone(value)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [count, path]
    )
  }

  /**
   * A hook to use in react components that provides the value setter for a specific path
   * @param {String} path
   */
  useSetter(path) {
    return React.useCallback(value => this.set(value, path), [path])
  }

  /**
   * A hook to use in react components that provides the value as a state (value + setter) for a specific path
   * @param {String} path
   * @param {*} defaultValue
   */
  useState(path, defaultValue) {
    return [this.useValue(path, defaultValue), this.useSetter(path)]
  }
}
